import React, { useEffect, useState } from 'react';
import { Radar, Plus, Trash2, Pencil, Loader2 } from 'lucide-react';
import { useStrategyLabStore } from '../../stores/strategyLabStore';
import type { Scanner } from '../../types/strategyLab';
import ScannerBuilder from '../../components/strategy-lab/ScannerBuilder';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

function conditionCount(node: Scanner['root']): number {
  let n = 0;
  const walk = (x: Scanner['root'] | { type: 'condition' }) => {
    if (x.type === 'condition') n += 1;
    else x.children.forEach(walk);
  };
  walk(node);
  return n;
}

const ScannersPage: React.FC = () => {
  const scanners = useStrategyLabStore((s) => s.scanners);
  const loading = useStrategyLabStore((s) => s.scannersLoading);
  const error = useStrategyLabStore((s) => s.scannersError);
  const fetchScanners = useStrategyLabStore((s) => s.fetchScanners);
  const deleteScanner = useStrategyLabStore((s) => s.deleteScanner);

  const [mode, setMode] = useState<'idle' | 'create' | 'edit'>('idle');
  const [editing, setEditing] = useState<Scanner | null>(null);

  useEffect(() => {
    fetchScanners();
  }, [fetchScanners]);

  const startCreate = () => {
    setEditing(null);
    setMode('create');
  };
  const startEdit = (scn: Scanner) => {
    setEditing(scn);
    setMode('edit');
  };
  const finishBuilder = () => {
    setMode('idle');
    setEditing(null);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.3fr]">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Radar className="w-5 h-5 text-primary" /> Scanners
          </h2>
          <Button size="sm" onClick={startCreate}>
            <Plus className="w-4 h-4" /> New
          </Button>
        </div>

        {error && <p className="text-sm text-rose-500">{error}</p>}

        {loading && scanners.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading scanners…
          </div>
        ) : scanners.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground text-sm">
              No scanners yet. Build one from combinable day-trading triggers.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {scanners.map((scn) => (
              <Card
                key={scn.id}
                className={`transition-colors ${editing?.id === scn.id ? 'border-primary' : ''}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-base">{scn.name}</CardTitle>
                    <Badge variant="secondary" className="shrink-0">
                      {conditionCount(scn.root)} trigger{conditionCount(scn.root) === 1 ? '' : 's'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => startEdit(scn)}>
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-rose-500 hover:text-rose-600"
                    onClick={() => {
                      if (window.confirm(`Delete scanner “${scn.name}”?`)) {
                        deleteScanner(scn.id);
                        if (editing?.id === scn.id) finishBuilder();
                      }
                    }}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <div>
        {mode === 'idle' ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center text-muted-foreground text-sm space-y-3">
              <p>Select a scanner to edit, or build a new one and test-scan it live.</p>
              <Button variant="outline" size="sm" onClick={startCreate}>
                <Plus className="w-4 h-4" /> New scanner
              </Button>
            </CardContent>
          </Card>
        ) : (
          <ScannerBuilder editing={mode === 'edit' ? editing : null} onDone={finishBuilder} />
        )}
      </div>
    </div>
  );
};

export default ScannersPage;
